import { ReactChild } from 'react'

import { Root, Indicator } from '@radix-ui/react-checkbox'
import { styled } from 'styled-components'

import Spinner from 'core/components/lib/Spinner'
import { PendingEventHandler, usePendingCallback } from 'core/hooks'
import { Field } from 'core/hooks/useForm'

type CheckboxProps<T extends boolean | 'indeterminate'> = Omit<
  Field<T>,
  'onChange'
> & {
  className?: string
  /** Calls the provided function when activated. Return a promise to show a spinner while the promise resolves. */
  onChange?: PendingEventHandler<T>
  /** The text to display alongside the checkbox. */
  label?: ReactChild
  /** The text to display underneath the checkbox and label. */
  sublabel?: ReactChild
  /** The text for accessibility for when `label` is not used. */
  ariaLabel?: string
  /** Replaces the checkbox with a spinner while true. */
  isLoading?: boolean
}

/** A single Checkbox with a label and sublabel. */
const Checkbox = <T extends boolean | 'indeterminate'>({
  value,
  name,
  disabled,
  onChange: _onChange,
  label,
  sublabel,
  ariaLabel,
  isLoading,
  className,
}: CheckboxProps<T>) => {
  const [isPending, onChange] = usePendingCallback(_onChange)

  return (
    <Box $disabled={disabled} className={className}>
      {isPending || isLoading ? (
        <Spinner height='20px' width='20px' />
      ) : (
        <StyledRoot
          aria-label={ariaLabel}
          checked={value}
          disabled={disabled}
          id={name}
          name={name}
          onCheckedChange={onChange}
          value={value?.toString() ?? 'false'}
        >
          <StyledIndicator forceMount />
        </StyledRoot>
      )}
      {!!label && <Label htmlFor={name}>{label}</Label>}
      {!!sublabel && <SubLabel>{sublabel}</SubLabel>}
    </Box>
  )
}

export default Checkbox

const Box = styled.div<{ $disabled: boolean | undefined }>`
  display: grid;
  grid-template-columns: min-content 1fr;
  gap: 0 8px;
  align-items: center;

  button,
  label {
    cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  }
`

const StyledRoot = styled(Root)`
  box-sizing: border-box;
  position: relative;
  border: 2px solid ${(p) => p.theme.border};
  border-radius: 10%;
  background-color: ${(p) => p.theme.background};
  width: 20px;
  height: 20px;

  &[data-state='checked'] {
    border-color: ${(p) => p.theme.primary};
    background-color: ${(p) => p.theme.primary};
  }

  &[data-disabled] {
    border-color: ${(p) => p.theme.disabledText};
    background-color: ${(p) => p.theme.disabledText};
  }
`

const StyledIndicator = styled(Indicator)`
  box-sizing: border-box;
  position: absolute;
  right: 9.5px;
  bottom: 4px;
  transform: rotate(45deg);
  transform-origin: bottom right;
  opacity: 0;
  border-right: 2px solid white;
  border-bottom: 2px solid white;
  border-radius: 10%;
  width: 0;
  height: 0;

  &[data-state='checked'] {
    transition: all 0.2s ease-in;
    opacity: 1;
    width: 6px;
    height: 10px;
  }

  &[data-state='indeterminate'] {
    right: 3px;
    bottom: 7px;
    transform: none;
    transition: all 0.2s ease-in;
    opacity: 1;
    border-color: ${(p) => p.theme.border};
    width: 10px;
    height: 0;

    &[data-disabled] {
      border-color: ${(p) => p.theme.background};
    }
  }
`

const Label = styled.label`
  cursor: pointer;
  color: ${(p) => p.theme.text};
  font-size: 14px;
`

export const SubLabel = styled.span`
  grid-column: 2;
  margin-bottom: 8px;
  color: ${(p) => p.theme.disabledText};
  font-size: 14px;
`
